import { useEffect, useState } from "react";
import { OfflineItemService } from "../../../../../services/api/OfflineOrder/offline.service";
import { useAuth } from "../../../../../context/AuthContext";
import { PatientFormData } from "./OfflineMain";

interface PatientDetailsFormProps {
  formData: PatientFormData;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  setFormData: React.Dispatch<React.SetStateAction<PatientFormData>>;
  setExistingCoins: React.Dispatch<React.SetStateAction<number>>;
}

const PatientDetailsForm = ({
  formData,
  handleInputChange,
  setFormData,
  setExistingCoins,
}: PatientDetailsFormProps) => {
  const [, setLoading] = useState(false);
  const [, setError] = useState<null | string>(null);
  const [validationErrors, setValidationErrors] = useState({
    patientName: "",
    patientMobile: "",
    drName: "",
    patientPincode: "",
  });

  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";

  useEffect(() => {
    const fetchClinicDetails = async () => {
      try {
        if (!clinicId) return;

        setLoading(true);
        setError(null);

        const response = await OfflineItemService.getClinicDetailsById(
          clinicId
        );
        const clinicData = response.data[0] || {};
        setFormData(clinicData);
      } catch (err) {
        console.error("Failed to load clinic details:", err);
        setError("Failed to load clinic details");
      } finally {
        setLoading(false);
      }
    };

    fetchClinicDetails();
  }, [clinicId]);

  // const getClinicGSTNumber = async (pincode:string) => {
  //   try {
  //     const response = await OfflineItemService.getClinicGSTNByPincode(pincode);

  //   } catch (error) {
  //     console.log("Failed to fetch GSTN:", error);
  //     setError("Failed to fetch GSTN information");
  //   }
  // };

  // New function to fetch patient details by mobile number
  const fetchPatientDetails = async (mobileNumber: string) => {
    try {
      if (mobileNumber.length === 10 && /^\d{10}$/.test(mobileNumber)) {
        const response = await OfflineItemService.getPatientByNumber(
          mobileNumber
        );
        if (response.success && response.data) {
          const patientData = response.data;
          setFormData((prev: typeof formData) => ({
            ...prev,
            patientName: patientData.patient_name,
            patientAddress: patientData.patient_address,
            patientPincode: patientData.patient_pincode,
          }));
          setExistingCoins(patientData?.coins || 0);
          // Clear validation errors for auto-filled fields
          setValidationErrors((prev) => ({
            ...prev,
            patientName: "",
            patientPincode: "",
            patientMobile: "",
            patientAddress: "",
          }));
        }
      }
    } catch (error) {
      console.error("Failed to fetch patient details:", error);
    }
  };

  const validateField = (name: string, value: string) => {
    let error = "";

    switch (name) {
      case "patientName":
        if (!value) {
          error = "Patient name is required";
        }
        break;
      case "patientMobile":
        if (!value) {
          error = "Mobile number is required";
        } else if (!/^\d{10}$/.test(value)) {
          error = "Mobile number must be 10 digits";
        }
        break;
      case "patientPincode":
        if (!value) {
          error = "Patient pincode is required";
        } else if (!/^\d+$/.test(value)) {
          error = "Pincode must contain only numbers";
        } else if (value.length !== 6) {
          error = "Pincode must be exactly 6 digits";
        }
        break;
      case "drName":
        if (!value) {
          error = "Doctor name is required";
        }
        break;
      default:
        break;
    }

    setValidationErrors((prev) => ({
      ...prev,
      [name]: error,
    }));

    return !error;
  };

  const handleChangeWithValidation = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (name === "patientMobile") {
      // Only allow digits
      if (!/^\d*$/.test(value)) return;

      // Update the form data first to ensure the input reflects the change
      handleInputChange(e);

      // Then validate
      validateField(name, value);

      // If mobile number is complete (10 digits), fetch patient details
      if (value.length === 10) {
        await fetchPatientDetails(value);
      }
      return;
    }

    // For other fields
    handleInputChange(e);
    validateField(name, value);
  };

  // Rest of your component remains the same...
  return (
    <div className="w-full p-2 bg-white">
      <h2 className="text-xl font-semibold text-gray-800 mb-2">
        Patient Details
      </h2>

      <div className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div>
            <label
              htmlFor="patientMobile"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Patient Mobile Number
            </label>
            <input
              type="tel"
              id="patientMobile"
              name="patientMobile"
              value={formData.patientMobile}
              onChange={handleChangeWithValidation}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              placeholder="Enter Mobile Number"
              maxLength={10}
              className={`w-full px-3 py-2 border ${
                validationErrors.patientMobile
                  ? "border-red-500"
                  : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {validationErrors.patientMobile && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.patientMobile}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="patientName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Patient Name
            </label>
            <input
              type="text"
              id="patientName"
              name="patientName"
              value={formData?.patientName}
              onChange={handleChangeWithValidation}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              placeholder="Enter Patient Name"
              className={`w-full px-3 py-2 border ${
                validationErrors.patientName
                  ? "border-red-500"
                  : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {validationErrors.patientName && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.patientName}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="drName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Dr Name
            </label>
            <input
              type="text"
              id="drName"
              name="drName"
              value={formData.drName}
              onChange={handleChangeWithValidation}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              placeholder="Enter Doctor Name"
              className={`w-full px-3 py-2 border ${
                validationErrors.drName ? "border-red-500" : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {validationErrors.drName && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.drName}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor="patientAddress"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Patient Address
            </label>
            <input
              id="patientAddress"
              name="patientAddress"
              value={formData?.patientAddress}
              onChange={handleChangeWithValidation}
              placeholder="Enter Address"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label
              htmlFor="patientPincode"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Patient Pincode
            </label>
            <input
              type="text"
              id="patientPincode"
              name="patientPincode"
              value={formData?.patientPincode}
              onChange={handleChangeWithValidation}
              onBlur={(e) => validateField(e.target.name, e.target.value)}
              placeholder="Enter Pincode"
              maxLength={6}
              className={`w-full px-3 py-2 border ${
                validationErrors.patientPincode
                  ? "border-red-500"
                  : "border-gray-300"
              } rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {validationErrors.patientPincode && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.patientPincode}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailsForm;
